import React, { useEffect, useState } from "react";
import {
  AppPermissions,
  Customer,
  Dict,
  FilterValueType,
  getFilters,
  Lead,
  LeadDetails,
  ListHeader,
  ListHeaderSearch,
  ListSort,
  ListTable,
  ListTableColumn,
  PaginatedApiResponse,
  QueryFilter,
  SortOrder,
  useHasPermission,
} from "../../../../core";
import moment from "moment";
import queryString, { ParsedQuery } from "query-string";
import { useLocation } from "react-router";
import api from "../../../../api";
import { useDataLoader } from "react-remote-data-hooks";
import {
  IDateRange,
} from "../../../../core/components/list-filters/DateRangeListFilter";
import Pagination from "../../../../core/components/pagination/Pagination";
import { CompanyAvatar } from "../../../../components/render-file/CompanyAvatar";
import userNoImage from "../../../../assets/images/user-noimage.png";
import MergeReviewCustomer from "./MergeReviewCustomer";
import { uniqBy } from "lodash";
import { ContainerLoader } from "../../../../components/loader";
import DeleteIcon from "../../../../assets/images/delete-icon.svg";
import UpwardArrowIcon from "../../../../assets/images/upward-arrow";
import { PersonColumns, SelectedColumns } from "./merge-customer-columns-header";

interface MergeCustomerProps {
  onReload?: () => void;
  onClose: () => void;
  className?: string;
  leadDetails?: LeadDetails;
}

const MergeCustomer: React.FunctionComponent<MergeCustomerProps> = ({
  onClose,
  className,
  leadDetails,
  onReload,
}) => {
  const canViewLeadDetails = useHasPermission(AppPermissions.ViewLeadDetails);
  const canBulkAssignLeads = useHasPermission(AppPermissions.BulkAssignLeads);
  const params = useLocation().search;
  const [search, setSearch] = useState("");
  const [showSelectedCustomers, setSelectedCustomers] = useState(false);
  const [reviewMerge, setReviewMerge] = useState<boolean>(false);

  const loadFromQuery = () => {
    const queryFilter: QueryFilter = {};
    const queries: ParsedQuery<string> = queryString.parse(params);
    queryFilter.query = queries.query as string;
    if (queries.filters) {
      queryFilter.filters = JSON.parse(queries.filters?.toString());
    }
    if (queries.page) {
      queryFilter.page = Number(queries.page || 1);
    }
    if (queries.perPage) {
      queryFilter.perPage = Number(queries.perPage || 1);
    }
    if (queries.startDate) {
      queryFilter.startDate = moment(queries.startDate as string).toDate();
    }
    if (queries.endDate) {
      queryFilter.endDate = moment(queries.endDate as string).toDate();
    }

    if (queries.sort) {
      queryFilter.sort = JSON.parse(queries.sort?.toString());
    }

    return queryFilter;
  };

  const [filters, setFilters] = useState<Dict<FilterValueType>>(
    loadFromQuery()?.filters || {}
  );
  const [sort, setSort] = useState<ListSort>({
    sortBy: loadFromQuery()?.sort?.sortBy || "createdAt",
    sortOrder: loadFromQuery()?.sort?.sortOrder || SortOrder.DESC,
  });
  const [dateFilters, setDateFilters] = useState<IDateRange>({
    startDate: loadFromQuery()?.startDate,
    endDate: loadFromQuery()?.endDate,
  });
  const [page, setPage] = useState(loadFromQuery()?.page || 1);
  const [perPage, setPerPage] = useState(loadFromQuery()?.perPage || 20);

  const { data, loading, loaded, reload } = useDataLoader<
    PaginatedApiResponse<Customer> & { lastUpdatedAt: Date }
  >(
    (
      searchTerm: string | undefined,
      searchFilters: Dict<FilterValueType>,
      currentPage: number,
      pageCount: number,
      searchOrder?: ListSort,
      dates?: IDateRange
    ) =>
      api.customer.getCustomers({
        fromDate: dates?.startDate
          ? moment(dates?.startDate).startOf("day").toDate()
          : undefined,
        toDate: dates?.endDate
          ? moment(dates?.endDate).endOf("day").toDate()
          : undefined,
        ...getFilters(searchFilters),
        page: currentPage,
        query: searchTerm,
        sortBy: searchOrder?.sortBy,
        sortOrder: searchOrder?.sortOrder,
        perPage: pageCount,
      }),
    [search, filters, page, perPage, sort, dateFilters]
  );
  useEffect(() => {
    if (loaded) {
      setPage(1);
      reload(search, filters, 1, perPage, sort, dateFilters);
    }
  }, [search, filters, sort, perPage, dateFilters]);

  useEffect(() => {
    if (loaded) {
      reload(search, filters, page, perPage, sort, dateFilters);
    }
  }, [page]);

  useEffect(() => {
    const filterResonse = loadFromQuery();

    if (filterResonse.query) {
      setSearch(filterResonse.query as string);
    }
    if (filterResonse.filters) {
      setFilters(filterResonse.filters);
    }
    if (filterResonse.page) {
      setPage(filterResonse.page);
    }
    if (filterResonse.perPage) {
      setPerPage(filterResonse.perPage);
    }

    if (filterResonse.startDate && filterResonse.endDate) {
      setDateFilters({
        startDate: filterResonse.startDate,
        endDate: filterResonse.endDate,
      });
    }
  }, []);

  const [selected, setSelected] = useState<Customer[]>([]);
  const [selectedCustomersForMerge, setSelectedCustomersForMerge] = useState<Customer[]>([]);

  const onLeadClick = (id?: string) => {
    if (canViewLeadDetails) {
      // 
    }
  };


  const selectedColumns = [
    ...SelectedColumns,
    <ListTableColumn
      label=""
      source=""
      column=""
      settingsGroup=""
      formatContent={(lead) =>
        <div onClick={() => {
          setSelectedCustomersForMerge((prev) => prev.filter((leadInfo) => leadInfo?._id !== lead._id));
        }} role={'button'} className="text-primary fa-underline"><img src={DeleteIcon} alt="delete Icon" /></div>
      }
      cssClass="right-0 position-sticky bg-white width-40 delete-box"
    ></ListTableColumn>,
  ];

  const headerActions = [
    <ListHeaderSearch
      placeholder="Customer Name / Mobile/ Email"
      serachTerm={search}
      onSearch={(val: string) => {
        setSearch(val);
      }}
    />,
  ];

  return (
    <div className="modal-open">
      <div
        className="detail-popup bg-white position-fixed top-0 w-100 right-0 h-100 overflow-auto in"
        style={{ zIndex: 9999 }}
      >
        <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-99">
          <div className="row align-items-center">
            <div
              className="col-auto"
              onClick={() => {
                onClose?.();
              }}
            >
              <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
            </div>
            <div className="col">
              <h5 className="mb-0 py-2">Merge Customer</h5>
            </div>
          </div>
        </div>
        <div className={`${className || ""} merge-wrapper`}>
          <div className="row h-100 mx-0">
            <div className="col-sm-6 col-12  border-right px-0 merge-lead-left-wrap">
              <div className="border-bottom px-4 ">
                <div>
                  <ListHeader additionalRows={[]} actions={headerActions}>
                    <h5 className="font-normal font-weight-bold mb-0">
                      Select Customer
                    </h5>
                  </ListHeader>
                </div>
              </div>

              <div className="p-4">
                {loading ?
                  <div className="w-100 h-50 p-5">
                    <ContainerLoader></ContainerLoader>
                  </div>
                  :
                  <>
                    {loaded && !loading && (
                      <ListTable
                        data={data?.data.filter(
                          (item) => item._id !== leadDetails?.customer?._id
                        )}
                        showSelectOption={canBulkAssignLeads}
                        selectedValues={selected.map(item => item._id || '')}
                        keepSelectedIds={selected.map(item => item._id || '')}
                        onSelect={(ids: string[]) => {
                          const temp = [...selected, ...data?.data].filter(item => ids.includes(item._id || ''))
                          setSelected(uniqBy(temp, '_id'))
                        }}
                        onSort={(sortKey: string, sortOrder?: SortOrder) => {
                          setSort({ sortBy: sortKey, sortOrder });
                        }}
                        sort={sort}
                        onRowClick={(d: Lead) => onLeadClick(d._id)}
                        noMargin
                      >
                        {PersonColumns}
                      </ListTable>
                    )}
                  </>}
              </div>
              <div
                className="button-bottom-merge left-0"
                style={{ width: "49.4%" }}
              >
                <div>
                  <Pagination
                    currentPage={page}
                    totalPages={data?.totalPages}
                    onPageChange={(pg: number) => {
                      setPage(pg);
                    }}
                    perPage={perPage}
                    onPerPageChange={(pg: number) => {
                      setPerPage(pg);
                    }}
                    count={data?.count}
                    label="Customers"
                  />
                </div>
                <div className="d-flex justify-content-end mt-1">
                  <button
                    className="btn btn-primary"
                    style={{ minWidth: "136px" }}
                    onClick={() => {
                      setSelectedCustomers(true)
                      setSelectedCustomersForMerge(selected)
                    }}
                    disabled={selected.length <= 0}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-12 px-0 merge-lead-right-wrap">
              <div className="border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <div className="py-sm">
                  <h5 className="font-normal font-weight-bold mb-0" style={{ lineHeight: '1.15' }}>
                    Selected Customers ({selectedCustomersForMerge.length})
                  </h5>
                </div>
              </div>
              <div className="mt-3 pt-1">
                <div className="lead-merge-property-enquiry mx-auto">
                  <div className="user-details">
                    <div className="details p-3">
                      <div className="d-flex">
                        <figure className="mb-0">
                          {leadDetails?.company ? (
                            leadDetails?.company?.logo ? (
                              <CompanyAvatar
                                id={leadDetails?.company?._id}
                                className="img-thumbnail rounded-circle w-100 h-100"
                                alt={leadDetails?.company?.name}
                                title={leadDetails?.company?.name}
                                defaultImage={userNoImage}
                                imageUrl={leadDetails?.company?.logo?.location}
                                image={leadDetails?.company?.logo}
                              ></CompanyAvatar>
                            ) : (
                              <i className="icon-building font-lg"></i>
                            )
                          ) : (
                            <CompanyAvatar
                              className="img-thumbnail  rounded-circle w-100 h-100"
                              defaultImage={userNoImage}
                            ></CompanyAvatar>
                          )}
                        </figure>

                        <figcaption className="ml-3 d-flex d-flex flex-column justify-content-center">
                          <h6 className="font-normal font-weight-semibold">
                            {leadDetails?.company?.name ||
                              leadDetails?.customer?.fullName}{" "}
                          </h6>
                          <p className="font-sm mb-0 d-flex">
                            {leadDetails?.company?.primaryPhone ||
                              leadDetails?.customer?.primaryPhone}{" "}
                            <span>
                              <span className="d-inline-block rounded-circle mx-1" style={{ width: "3px", height: "3px", backgroundColor: "#6F7171", verticalAlign: "middle" }}></span>{" "}
                              {leadDetails?.company?.primaryEmail ||
                                leadDetails?.customer?.primaryEmail}
                            </span>
                          </p>
                        </figcaption>
                      </div>
                    </div>
                  </div>
                  <div className="text-center pt-4 mt-2">
                    <UpwardArrowIcon />
                  </div>
                </div>
                <div className="px-4">
                  {selectedCustomersForMerge.length === 0 || showSelectedCustomers === false ? (
                    <p className="font-normal text-muted-light mt-4 pt-1 text-center">
                      No customers selected.{" "}
                      <span className="d-block pt-1">
                        Please choose a customer from the list to merge
                      </span>
                    </p>
                  ) : (
                    <div>
                      <div className="mt-4 mb-5 pb-5">
                        <ListTable
                          data={selectedCustomersForMerge}
                          sort={sort}
                          noMargin
                        >
                          {selectedColumns}
                        </ListTable>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div
                className="button-bottom-merge d-flex justify-content-end right-3"
                style={{ width: "49.4%" }}
              >
                <button
                  className="btn btn-primary px-4"
                  onClick={() => {
                    setReviewMerge(true);
                  }}
                  disabled={selectedCustomersForMerge.length <= 0}
                >
                  Review and Merge
                </button>
              </div>
              {reviewMerge && leadDetails?.customer && (
                <MergeReviewCustomer
                  onClose={() => {
                    setReviewMerge(false)
                    onReload?.()
                    onClose()
                  }}
                  onBack={() => {
                    setReviewMerge(false)
                  }}
                  customer={leadDetails?.customer}
                  mergingCustomers={selectedCustomersForMerge}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );
};

export default MergeCustomer;
